import { useState, useEffect } from "react";
import axios from "axios";
import {
  Save, Upload, Loader2, Image, Settings, Phone, Mail, Building2, Stamp, Globe, Hash
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import AdminLayout from "@/components/AdminLayout";
import { API } from "@/lib/api";

const TABS = [
  { key: 'general', label: 'Obecné', icon: Settings },
  { key: 'contact', label: 'Kontakt', icon: Phone },
  { key: 'company', label: 'Firma', icon: Building2 },
  { key: 'media', label: 'Média', icon: Image },
  { key: 'email', label: 'E-mail', icon: Mail },
  { key: 'numbering', label: 'Číslování', icon: Hash },
];

export default function AdminSettingsPage() {
  const { token } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({});
  const [activeTab, setActiveTab] = useState('general');

  useEffect(() => {
    axios.get(`${API}/settings`)
      .then(res => setSettings(res.data))
      .catch(() => toast.error('Chyba načítání nastavení'))
      .finally(() => setLoading(false));
  }, []);

  const update = (key, val) => setSettings(prev => ({ ...prev, [key]: val }));

  const saveSettings = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(res.data);
      toast.success('Nastavení uloženo');
    } catch {
      toast.error('Chyba ukládání');
    } finally {
      setSaving(false);
    }
  };

  const uploadFile = async (type, file) => {
    setUploading(prev => ({ ...prev, [type]: true }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/admin/upload/${type}`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      const urlKey = `${type}_url`;
      setSettings(prev => ({ ...prev, [urlKey]: res.data[urlKey] }));
      toast.success('Soubor nahrán');
    } catch {
      toast.error('Chyba nahrávání');
    } finally {
      setUploading(prev => ({ ...prev, [type]: false }));
    }
  };

  const resetOrderNumber = async () => {
    if (!window.confirm('Opravdu resetovat číselnou řadu? Další objednávka začne od nastaveného počátečního čísla.')) return;
    try {
      await axios.post(`${API}/admin/reset-order-number`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Číselná řada resetována');
    } catch {
      toast.error('Chyba resetování');
    }
  };

  if (loading) return (
    <AdminLayout active="settings">
      <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#004E98]" /></div>
    </AdminLayout>
  );

  return (
    <AdminLayout active="settings">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-slate-800" data-testid="admin-settings">Nastavení e-shopu</h1>
        <button onClick={saveSettings} disabled={saving} className="h-10 px-5 bg-[#004E98] text-white rounded text-sm font-medium flex items-center gap-2 hover:bg-[#003B73] disabled:opacity-50 transition-colors" data-testid="save-all-settings-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Uložit vše
        </button>
      </div>

      <div className="flex flex-wrap gap-1 mb-6 bg-slate-200 rounded-lg p-1" data-testid="settings-tabs">
        {TABS.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)} className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === tab.key ? 'bg-white text-[#004E98] shadow-sm' : 'text-slate-600 hover:text-slate-800'}`} data-testid={`tab-${tab.key}`}>
            <tab.icon className="w-4 h-4" />{tab.label}
          </button>
        ))}
      </div>

      <div className="max-w-3xl">
        {activeTab === 'general' && (
          <div className="space-y-6" data-testid="tab-general-content">
            <div className="bg-white border border-slate-200 rounded-lg p-6">
              <h2 className="font-semibold text-slate-800 mb-4 flex items-center gap-2"><Globe className="w-5 h-5" /> Základní informace</h2>
              <div><label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Název webu</label>
                <input type="text" value={settings.site_name || ''} onChange={e => update('site_name', e.target.value)} className="w-full h-11 border border-slate-300 rounded px-4" data-testid="site-name-input" />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'contact' && (
          <div className="space-y-6" data-testid="tab-contact-content">
            <div className="bg-white border border-slate-200 rounded-lg p-6">
              <h2 className="font-semibold text-slate-800 mb-4 flex items-center gap-2"><Phone className="w-5 h-5" /> Kontaktní údaje</h2>
              <p className="text-sm text-slate-500 mb-4">Tyto údaje se zobrazují v hlavičce a patičce webu.</p>
              <div className="grid md:grid-cols-2 gap-4">
                <div><label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Telefon</label>
                  <input type="text" value={settings.contact_phone || ''} onChange={e => update('contact_phone', e.target.value)} className="w-full h-11 border border-slate-300 rounded px-4" placeholder="+420 123 456 789" data-testid="contact-phone-input" /></div>
                <div><label className="text-xs font-bold uppercase text-slate-500 mb-1 block">E-mail</label>
                  <input type="email" value={settings.contact_email || ''} onChange={e => update('contact_email', e.target.value)} className="w-full h-11 border border-slate-300 rounded px-4" placeholder="info@example.cz" data-testid="contact-email-input" /></div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'company' && (
          <div className="space-y-6" data-testid="tab-company-content">
            <div className="bg-white border border-slate-200 rounded-lg p-6">
              <h2 className="font-semibold text-slate-800 mb-4 flex items-center gap-2"><Building2 className="w-5 h-5" /> Firemní údaje</h2>
              <p className="text-sm text-slate-500 mb-4">Tyto údaje se používají na fakturách a v patičce webu.</p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2"><label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Název firmy</label>
                  <input type="text" value={settings.company_name || ''} onChange={e => update('company_name', e.target.value)} className="w-full h-11 border border-slate-300 rounded px-4" data-testid="company-name-input" /></div>
                <div className="md:col-span-2"><label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Adresa</label>
                  <input type="text" value={settings.company_address || ''} onChange={e => update('company_address', e.target.value)} className="w-full h-11 border border-slate-300 rounded px-4" data-testid="company-address-input" /></div>
                <div><label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Město</label>
                  <input type="text" value={settings.company_city || ''} onChange={e => update('company_city', e.target.value)} className="w-full h-11 border border-slate-300 rounded px-4" data-testid="company-city-input" /></div>
                <div><label className="text-xs font-bold uppercase text-slate-500 mb-1 block">PSČ</label>
                  <input type="text" value={settings.company_zip || ''} onChange={e => update('company_zip', e.target.value)} className="w-full h-11 border border-slate-300 rounded px-4" data-testid="company-zip-input" /></div>
                <div><label className="text-xs font-bold uppercase text-slate-500 mb-1 block">IČO</label>
                  <input type="text" value={settings.company_ico || ''} onChange={e => update('company_ico', e.target.value)} className="w-full h-11 border border-slate-300 rounded px-4" data-testid="company-ico-input" /></div>
                <div><label className="text-xs font-bold uppercase text-slate-500 mb-1 block">DIČ</label>
                  <input type="text" value={settings.company_dic || ''} onChange={e => update('company_dic', e.target.value)} className="w-full h-11 border border-slate-300 rounded px-4" data-testid="company-dic-input" /></div>
                <div><label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Země</label>
                  <input type="text" value={settings.company_country || ''} onChange={e => update('company_country', e.target.value)} className="w-full h-11 border border-slate-300 rounded px-4" data-testid="company-country-input" /></div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'media' && (
          <div className="space-y-6" data-testid="tab-media-content">
            <MediaUploadCard title="Logo" icon={<Image className="w-5 h-5" />} description="Logo webu zobrazené v hlavičce. Doporučený formát PNG s průhledným pozadím." currentUrl={settings.logo_url} uploading={uploading.logo} onUpload={(file) => uploadFile('logo', file)} testId="logo" />
            <MediaUploadCard title="Favicon" icon={<Globe className="w-5 h-5" />} description="Ikona zobrazená v záložce prohlížeče. Doporučený formát ICO nebo PNG (32×32px)." currentUrl={settings.favicon_url} uploading={uploading.favicon} onUpload={(file) => uploadFile('favicon', file)} testId="favicon" small />
            <MediaUploadCard title="Razítko" icon={<Stamp className="w-5 h-5" />} description="Razítko firmy zobrazené na fakturách. Doporučený formát PNG s průhledným pozadím." currentUrl={settings.stamp_url} uploading={uploading.stamp} onUpload={(file) => uploadFile('stamp', file)} testId="stamp" />
          </div>
        )}

        {activeTab === 'email' && (
          <div className="space-y-6" data-testid="tab-email-content">
            <div className="bg-white border border-slate-200 rounded-lg p-6">
              <h2 className="font-semibold text-slate-800 mb-4 flex items-center gap-2"><Mail className="w-5 h-5" /> SMTP nastavení</h2>
              <p className="text-sm text-slate-500 mb-4">Nastavte SMTP server pro odesílání e-mailových notifikací (potvrzení objednávky, změna stavu, odeslání zásilky s přiloženou fakturou v PDF).</p>
              <div className="grid md:grid-cols-2 gap-4">
                <div><label className="text-xs font-bold uppercase text-slate-500 mb-1 block">SMTP Host</label>
                  <input type="text" value={settings.smtp_host || ''} onChange={e => update('smtp_host', e.target.value)} className="w-full h-11 border border-slate-300 rounded px-4" placeholder="smtp.gmail.com" data-testid="smtp-host-input" /></div>
                <div><label className="text-xs font-bold uppercase text-slate-500 mb-1 block">SMTP Port</label>
                  <input type="text" value={settings.smtp_port || '587'} onChange={e => update('smtp_port', e.target.value)} className="w-full h-11 border border-slate-300 rounded px-4" placeholder="587" data-testid="smtp-port-input" /></div>
                <div><label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Uživatel</label>
                  <input type="text" value={settings.smtp_user || ''} onChange={e => update('smtp_user', e.target.value)} className="w-full h-11 border border-slate-300 rounded px-4" placeholder="user@example.com" data-testid="smtp-user-input" /></div>
                <div><label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Heslo</label>
                  <input type="password" value={settings.smtp_pass || ''} onChange={e => update('smtp_pass', e.target.value)} className="w-full h-11 border border-slate-300 rounded px-4" placeholder="••••••" data-testid="smtp-pass-input" /></div>
                <div className="md:col-span-2"><label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Odesílatel (From)</label>
                  <input type="email" value={settings.smtp_from || ''} onChange={e => update('smtp_from', e.target.value)} className="w-full h-11 border border-slate-300 rounded px-4" placeholder="noreply@example.com" data-testid="smtp-from-input" /></div>
              </div>
              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded text-sm text-amber-700">
                <strong>Poznámka:</strong> Bez nastavení SMTP se e-maily pouze zaznamenávají do logu serveru. Po vyplnění se budou odesílat skutečné e-maily včetně PDF faktury v příloze.
              </div>
            </div>
          </div>
        )}

        {activeTab === 'numbering' && (
          <div className="space-y-6" data-testid="tab-numbering-content">
            <div className="bg-white border border-slate-200 rounded-lg p-6">
              <h2 className="font-semibold text-slate-800 mb-4 flex items-center gap-2"><Hash className="w-5 h-5" /> Číselná řada objednávek</h2>
              <p className="text-sm text-slate-500 mb-4">Nastavte prefix a počáteční číslo pro objednávky. Formát: PREFIX + pořadové číslo (např. OB-000001).</p>
              <div className="grid md:grid-cols-2 gap-4">
                <div><label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Prefix objednávky</label>
                  <input type="text" value={settings.order_prefix || ''} onChange={e => update('order_prefix', e.target.value)} className="w-full h-11 border border-slate-300 rounded px-4" placeholder="OB-" data-testid="order-prefix-input" /></div>
                <div><label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Počáteční číslo</label>
                  <input type="number" value={settings.order_start_number || ''} onChange={e => update('order_start_number', e.target.value)} className="w-full h-11 border border-slate-300 rounded px-4" placeholder="1" data-testid="order-start-number-input" /></div>
              </div>
              <div className="mt-4 flex items-center gap-3">
                <button onClick={resetOrderNumber} className="h-10 px-4 border border-red-300 text-red-600 rounded text-sm font-medium hover:bg-red-50 transition-colors" data-testid="reset-order-number-btn">
                  Resetovat číselnou řadu
                </button>
                <span className="text-sm text-slate-500">Další objednávka začne od počátečního čísla.</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function MediaUploadCard({ title, icon, description, currentUrl, uploading, onUpload, testId, small }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6">
      <h2 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">{icon} {title}</h2>
      <div className="flex items-center gap-6">
        <div className={`${small ? 'w-16 h-16' : 'w-48 h-24'} bg-slate-100 border border-slate-200 rounded flex items-center justify-center overflow-hidden`}>
          {currentUrl ? (
            <img src={currentUrl} alt={title} className="max-w-full max-h-full object-contain" data-testid={`current-${testId}`} />
          ) : (
            <span className="text-xs text-slate-400">Není nahráno</span>
          )}
        </div>
        <div>
          <p className="text-sm text-slate-500 mb-3">{description}</p>
          <label className={`inline-flex items-center gap-2 h-10 px-4 bg-[#004E98] text-white rounded text-sm font-medium cursor-pointer hover:bg-[#003B73] transition-colors ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Nahrát {title.toLowerCase()}
            <input type="file" accept="image/*" onChange={e => e.target.files?.[0] && onUpload(e.target.files[0])} className="hidden" data-testid={`${testId}-upload-input`} />
          </label>
        </div>
      </div>
    </div>
  );
}
